import config from "../config";
import { emitAuthSessionChange, hasAuthSession, readAuthSession, setStoredAuthSession } from "../helpers/authSession";
import { sanitizeStoredAuthUser } from "../helpers/authUserStorage";
import browserHistory from "../helpers/browserHistory";
import HttpClient, { resetToken } from "../helpers/httpclient";
import { appModal } from "../helpers/interactions";

export const AUTH_AUTHENTICATING = "AUTH_AUTHENTICATING";
export const AUTH_SUCCESS = "AUTH_SUCCESS";
export const AUTH_FAILED = "AUTH_FAILED";
export const AUTH_LOCKED = "AUTH_LOCKED";
export const AUTH_SIGNING_OUT = "AUTH_SIGNING_OUT";
export const AUTH_SIGNING_OUT_SUCCESS = "AUTH_SIGNING_OUT_SUCCESS";
export const AUTH_SIGNING_OUT_FAILED = "AUTH_SIGNING_OUT_FAILED";
export const AUTH_CHANGING_PASSWORD = "AUTH_CHANGING_PASSWORD";
export const AUTH_CHANGE_PASSWORD_SUCCESS = "AUTH_CHANGE_PASSWORD_SUCCESS";
export const AUTH_CHANGE_PASSWORD_FAILED = "AUTH_CHANGE_PASSWORD_FAILED";
export const AUTH_RESETTING_PASSWORD = "AUTH_RESETTING_PASSWORD";
export const AUTH_RESET_PASSWORD_SUCCESS = "AUTH_RESET_PASSWORD_SUCCESS";
export const AUTH_RESET_PASSWORD_FAILED = "AUTH_RESET_PASSWORD_FAILED";

export const AUTH_2FA_REQUIRED = "AUTH_2FA_REQUIRED";
export const AUTH_2FA_AUTHENTICATING = "AUTH_2FA_AUTHENTICATING";
export const AUTH_2FA_FAILED = "AUTH_2FA_FAILED";
export const AUTH_2FA_EXPIRED = "AUTH_2FA_EXPIRED";
export const AUTH_2FA_RESENDING = "AUTH_2FA_RESENDING";
export const AUTH_2FA_RESEND_SUCCESS = "AUTH_2FA_RESEND_SUCCESS";
export const AUTH_2FA_RESEND_FAILED = "AUTH_2FA_RESEND_FAILED";

export const AUTH_IMPERSONATING = "AUTH_IMPERSONATING";
export const AUTH_IMPERSONATE_SUCCESS = "AUTH_IMPERSONATE_SUCCESS";
export const AUTH_IMPERSONATE_FAILED = "AUTH_IMPERSONATE_FAILED";

export const AUTH_ENDIMPERSONATING = "AUTH_ENDIMPERSONATING";
export const AUTH_ENDIMPERSONATING_SUCCESS = "AUTH_ENDIMPERSONATE_SUCCESS";
export const AUTH_ENDIMPERSONATING_FAILED = "AUTH_ENDIMPERSONATING_FAILED";

let cachedAuthUser = null;
let cachedAuthUserSerialized = null;

export function identifyFS(user, impersonated) {
  const FS = window.FS;
  if (!FS?.identify || !user) {
    console.log("FS not found. Unable to identify user.");
    return;
  }
  if (impersonated) {
    FS.identify(`${user.id}-${impersonated.id}`, {
      displayName: `${user.first_name} ${user.last_name} as ${impersonated.first_name} ${impersonated.last_name}`,
      email: `${user.email} as ${impersonated.email}`,
    });
    return;
  }
  FS.identify(`${user.id}`, {
    displayName: `${user.first_name} ${user.last_name}`,
    email: user.email,
  });
}

export function anonymizeFS() {
  const FS = window.FS;
  if (!FS?.identify) {
    console.log("FS not found. Unable to anonymize user.");
    return;
  }
  FS.identify(false);
}

function hideImpersonationModals() {
  if (!window.bootstrap?.Modal) {
    return;
  }

  try {
    appModal("#ImpersonateModal, #ImpersonateAlertModal", "hide");
  } catch {
    console.warn("Unable to hide the impersonation modal after the session changed.");
  }
}

function safeAuthError(error) {
  return {
    message: "Authentication request failed.",
    response: {
      data: error?.response?.data ?? {},
      status: error?.response?.status,
    },
  };
}

function redirectAfterLogin() {
  const path = localStorage.getItem("_pathname") || "/";
  localStorage.removeItem("_pathname");
  const redirectTo = path === "/signin" || path === "/signout" ? "/" : path;
  browserHistory.push(redirectTo);
}

function authenticate(user) {
  return async (dispatch) => {
    dispatch({ type: AUTH_AUTHENTICATING });
    try {
      const response = await HttpClient.post(`${config.api}/auth`, user);
      const result = response.data;
      if (result.two_factor_enabled) {
        return dispatch({ type: "AUTH_2FA_REQUIRED", result });
      }
      dispatch({ type: AUTH_SUCCESS, result });
      saveSession(result);
      identifyFS(result.user);
      redirectAfterLogin();
    } catch (error) {
      dispatch({
        type: error.response?.status === 429 ? AUTH_LOCKED : AUTH_FAILED,
        error: safeAuthError(error),
      });
    }
  };
}

function resend2faCode(challenge) {
  return async (dispatch) => {
    dispatch({ type: AUTH_2FA_RESENDING });
    try {
      const response = await HttpClient.post(`${config.api}/resend2fa`, {
        email: challenge.email,
        user_id: challenge.user_id,
        token: challenge.token,
      });
      dispatch({ type: AUTH_2FA_RESEND_SUCCESS });
      return response.data;
    } catch (error) {
      dispatch({
        type: error.response?.data?.code === "TWO_FACTOR_CHALLENGE_INVALID" ? AUTH_2FA_EXPIRED : AUTH_2FA_RESEND_FAILED,
        error: safeAuthError(error),
      });
      return false;
    }
  };
}

function validate2FA(params) {
  return async (dispatch) => {
    dispatch({ type: AUTH_2FA_AUTHENTICATING });
    try {
      const payload = {
        user_id: params.user_id,
        token: params.token,
      };
      if (typeof params.recovery_code === "string" && params.recovery_code.trim()) {
        payload.recovery_code = params.recovery_code.trim();
      } else {
        payload.code = params.code;
      }
      const response = await HttpClient.post(`${config.api}/2fa`, payload);
      const result = response.data;
      dispatch({ type: AUTH_SUCCESS, result });
      saveSession(result);
      identifyFS(result.user);
      redirectAfterLogin();
    } catch (error) {
      dispatch({
        type: error.response?.data?.code === "TWO_FACTOR_CHALLENGE_INVALID" ? AUTH_2FA_EXPIRED : AUTH_2FA_FAILED,
        error: safeAuthError(error),
      });
    }
  };
}

function doChangePassword(id, password) {
  return async (dispatch) => {
    dispatch({ type: AUTH_CHANGING_PASSWORD });
    try {
      const response = await HttpClient.post(`${config.api}/change-password`, { password, token: id });
      const result = response.data;
      dispatch({ type: AUTH_CHANGE_PASSWORD_SUCCESS, result });
      resetToken("password-change");
      browserHistory.push("/signin?r=true");
    } catch (error) {
      dispatch({ type: AUTH_CHANGE_PASSWORD_FAILED, error: safeAuthError(error) });
    }
  };
}

function doResetPassword(email, redirect = true) {
  return async (dispatch) => {
    dispatch({ type: AUTH_RESETTING_PASSWORD });
    try {
      const response = await HttpClient.post(`${config.api}/reset-password`, {
        email,
      });
      const result = response.data;
      dispatch({ type: AUTH_RESET_PASSWORD_SUCCESS, result });
      if (redirect) {
        browserHistory.push("/signin");
      }
    } catch (error) {
      dispatch({ type: AUTH_RESET_PASSWORD_FAILED, error: safeAuthError(error) });
    }
  };
}

function doSignout() {
  return async (dispatch) => {
    dispatch({ type: AUTH_SIGNING_OUT });
    try {
      await HttpClient.delete(`${config.api}/auth/session`, { __logoutRequest: true });
      resetToken("signout");
      dispatch({ type: AUTH_SIGNING_OUT_SUCCESS, user: {} });
    } catch (error) {
      // An already-expired or already-revoked server session is an idempotent
      // logout success. Other failures may mean revocation never happened, so
      // retain the browser's authenticated state and let the user retry.
      if (error.response?.status === 401) {
        resetToken("signout");
        dispatch({ type: AUTH_SIGNING_OUT_SUCCESS, user: {} });
        return;
      }
      dispatch({ type: AUTH_SIGNING_OUT_FAILED, error: safeAuthError(error) });
    }
  };
}

export function doImpersonate(user) {
  return async (dispatch) => {
    browserHistory.push("/impersonating");
    dispatch({ type: AUTH_IMPERSONATING });
    anonymizeFS();
    const authenticated = getUser();
    try {
      const response = await HttpClient.post(`${config.api}/users/impersonate/${user.id}`);
      const result = response.data;
      dispatch({ type: AUTH_IMPERSONATE_SUCCESS, result });
      saveSession(result, result.impersonated);
      identifyFS(authenticated, result.user);
      hideImpersonationModals();
      // Land on the home page after impersonating. The admin's current page may be
      // off-limits to the impersonated (lower-privilege) user, so starting from
      // home avoids dropping them onto a forbidden page / "not authorized" route.
      browserHistory.push("/");
    } catch (error) {
      dispatch({ type: AUTH_IMPERSONATE_FAILED, error: safeAuthError(error) });
    }
  };
}

export function endImpersonate() {
  return async (dispatch) => {
    dispatch({ type: AUTH_ENDIMPERSONATING });
    anonymizeFS();
    try {
      const response = await HttpClient.delete(`${config.api}/users/end-impersonate`);
      const result = response.data;
      dispatch({ type: AUTH_ENDIMPERSONATING_SUCCESS, result });
      saveSession(result);
      identifyFS(result.user);
      browserHistory.push("/");
    } catch (error) {
      dispatch({ type: AUTH_ENDIMPERSONATING_FAILED, error: safeAuthError(error) });
    }
  };
}

export function signin(user) {
  return (dispatch, _getState) => {
    return dispatch(authenticate(user));
  };
}

export function validateCode(params) {
  return (dispatch, _getState) => {
    return dispatch(validate2FA(params));
  };
}

export function resendCode(challenge) {
  return (dispatch, _getState) => {
    return dispatch(resend2faCode(challenge));
  };
}

function saveSession(result, impersonated = null) {
  storeAuthUser(result.user);
  setStoredAuthSession(cachedAuthUser, impersonated === undefined ? null : impersonated);
  emitAuthSessionChange("session-commit");
  document.dispatchEvent(new Event("asset:update-user"));
}

export function saveUser(user) {
  storeAuthUser(user);
  document.dispatchEvent(new Event("asset:update-user"));
}

function storeAuthUser(user) {
  cachedAuthUser = sanitizeStoredAuthUser(user);
  cachedAuthUserSerialized = JSON.stringify(cachedAuthUser);
  setStoredAuthSession(cachedAuthUser, readAuthSession().impersonated);
}

export function signout() {
  return (dispatch, _getState) => {
    // eslint-disable-line
    return dispatch(doSignout());
  };
}

export function signedIn() {
  return hasAuthSession();
}

export function isImpersonated() {
  return Boolean(readAuthSession().impersonated);
}

export function getUser() {
  const user = readAuthSession().user;
  if (!user) {
    cachedAuthUser = null;
    cachedAuthUserSerialized = null;
    return null;
  }
  const serialized = JSON.stringify(user);
  if (serialized === cachedAuthUserSerialized) {
    return cachedAuthUser;
  }
  cachedAuthUser = sanitizeStoredAuthUser(user);
  cachedAuthUserSerialized = JSON.stringify(cachedAuthUser);
  setStoredAuthSession(cachedAuthUser, readAuthSession().impersonated);
  return cachedAuthUser;
}

export function changePassword(id, password) {
  return (dispatch, _getState) => {
    // eslint-disable-line
    return dispatch(doChangePassword(id, password));
  };
}

export function resetPassword(email, redirect = true) {
  return (dispatch, _getState) => {
    // eslint-disable-line
    return dispatch(doResetPassword(email, redirect));
  };
}

export function activateUser(user) {
  return async (dispatch) => {
    dispatch({ type: AUTH_CHANGING_PASSWORD });
    try {
      const activation = { password: user.password, token: user.activation_token };
      if (user.authorized_signer) {
        activation.advisor_consulting_agreement = user.advisor_consulting_agreement;
      } else {
        activation.terms_conditions = user.terms_conditions;
      }
      const response = await HttpClient.post(`${config.api}/activate`, activation);
      const result = response.data;
      dispatch({ type: AUTH_CHANGE_PASSWORD_SUCCESS, result });
      if (result.reauthenticate) {
        resetToken("activation-complete");
        browserHistory.push("/signin?r=true");
        return;
      }
      saveSession(result);
      identifyFS(result.user);
      browserHistory.push("/");
    } catch (error) {
      dispatch({ type: AUTH_CHANGE_PASSWORD_FAILED, error: safeAuthError(error) });
    }
  };
}
